import { parseSafeNumber, roundTo } from '@/shared/lib/number';
import { Opening, Partition, WallElement, RoomGeometry, RoomDims } from '@/shared/model';
import {
  NICHE_SLOPE_THRESHOLD_M,
  DEFAULT_PARTITION_THICKNESS_CM,
  CM_TO_M,
  PARTITION_SIDES,
  COLUMN_ENDS_COUNT,
} from '@/shared/lib/geometry-constants';

/**
 * Рассчитывает геометрию комнаты (Pure Math Logic)
 */
export const calculateRoomGeometry = (
  length: number,
  width: number,
  height: number,
  openings: Opening[] = [],
  partitions: Partition[] = [],
  wallElements: WallElement[] = []
): RoomGeometry => {
  const l = Math.max(0, parseSafeNumber(length));
  const w = Math.max(0, parseSafeNumber(width));
  const h = Math.max(0, parseSafeNumber(height));

  const floorArea = l * w;
  const basePerimeter = (l + w) * 2;
  
  let openingsArea = 0;
  let windowSlopes = 0;
  let doorSlopes = 0;
  let doorsWidthTotal = 0;

  if (Array.isArray(openings)) {
    openings.forEach(op => {
      const opW = parseSafeNumber(op.width);
      const opH = parseSafeNumber(op.height);
      // parseSafeNumber guarantees finite numeric output (0 for unparseable) — no NaN guard needed.
      openingsArea += opW * opH;
      if (op.type === 'door') doorsWidthTotal += opW;
      if (op.hasSlopes !== false) {
        const slopeLen = opH * 2 + opW;
        if (op.type === 'window') windowSlopes += slopeLen;
        else if (op.type === 'door') doorSlopes += slopeLen;
      }
    });
  }

  let partitionsArea = 0;
  let partitionsPerimeter = 0;
  let partitionsEndsLinear = 0;

  if (Array.isArray(partitions)) {
    partitions.forEach(p => {
       const pL = parseSafeNumber(p.length);
       const pH = (p.height !== undefined && p.height !== null) ? parseSafeNumber(p.height) : h;
       const pThick = parseSafeNumber(p.thickness, DEFAULT_PARTITION_THICKNESS_CM) / CM_TO_M;
       const pEnds = parseInt(String(p.ends)) || 0;

       if (pEnds === COLUMN_ENDS_COUNT) {
           const columnPerimeter = pL * COLUMN_ENDS_COUNT;
           partitionsArea += columnPerimeter * pH;
           partitionsPerimeter += columnPerimeter;
       } else {
           const sides = PARTITION_SIDES;
           partitionsArea += pL * pH * sides;
           const perimeterEndsCount = pEnds > 2 ? 2 : pEnds;
           const attachments = Math.max(0, 2 - pEnds);
           partitionsPerimeter += (pL * sides) + (pThick * perimeterEndsCount) - (pThick * attachments);
       }
       partitionsEndsLinear += pH * pEnds;
    });
  }

  // --- NICHES & LEDGES CALCULATION ---
  let nichesArea = 0;
  let ledgesArea = 0;
  let elementsPerimeterDelta = 0;
  let elementsSlopesLinear = 0;
  let floorAreaDelta = 0;

  if (Array.isArray(wallElements)) {
    wallElements.forEach(el => {
        if (el.includeInTotal === false) return;

        const eW = parseSafeNumber(el.width);
        const eH = parseSafeNumber(el.height) || h;
        const eD = parseSafeNumber(el.depth);
        
        // 1. Internal/External surfaces
        // Logic: side1 + side2 + back/front. 
        // If surface > 0.5m, it goes to Area, else to Slopes (Linear)
        const surfaces = [
            { w: eD, h: eH }, // Side 1
            { w: eD, h: eH }, // Side 2
            { w: eW, h: eH }  // Front/Back
        ];

        let elementArea = 0;
        surfaces.forEach(s => {
            if (s.w >= NICHE_SLOPE_THRESHOLD_M) elementArea += s.w * s.h;
            else elementsSlopesLinear += s.h;
        });

        if (el.type === 'niche') {
            nichesArea += elementArea;
            // Subtract the "hole" from the main wall area
            nichesArea -= (eW * eH); 
            if (el.isFullHeight) floorAreaDelta += (eW * eD);
        } else {
            ledgesArea += elementArea;
            if (el.isFullHeight) floorAreaDelta -= (eW * eD);
        }

        elementsPerimeterDelta += (eD * 2);
    });
  }

  const wallArea = Math.max(0, (basePerimeter * h) - openingsArea + partitionsArea + nichesArea + ledgesArea);
  const finalFloorArea = Math.max(0, floorArea + floorAreaDelta);
  const perimeter = Math.max(0, basePerimeter + partitionsPerimeter - doorsWidthTotal + elementsPerimeterDelta);

  return {
    floorArea: roundTo(finalFloorArea, 2),
    wallArea: roundTo(wallArea, 2),
    perimeter: roundTo(perimeter, 2),
    height: h,
    openingsArea: roundTo(openingsArea, 2),
    partitionsArea: roundTo(partitionsArea, 2),
    windowSlopes: roundTo(windowSlopes, 2),
    doorSlopes: roundTo(doorSlopes, 2),
    partitionsEndsLinear: roundTo(partitionsEndsLinear + elementsSlopesLinear, 2),
    nichesArea: roundTo(nichesArea, 2),
    ledgesArea: roundTo(ledgesArea, 2)
  };
};

/**
 * SAFE-02 (closes a Warning carried from the Phase 33 review): returns the first
 * of `tiers`, in priority order, that is a finite number — never `NaN` or
 * `Infinity`/`-Infinity`. A non-finite tier is treated exactly like an absent
 * one and skipped in favour of the next tier. Zero IS finite, so a literal
 * zero override still wins over lower tiers — the Phase 33 locked
 * zero-is-a-value semantics are unaffected.
 */
const firstFinite = (...tiers: Array<number | undefined>): number | undefined =>
  tiers.find((t): t is number => Number.isFinite(t));

/**
 * Разрешает эффективную геометрию комнаты — единственное место, где ручные
 * оверрайды (manualFloorArea/manualWallArea/manualPerimeter) объединяются с
 * посчитанной геометрией. Трёхуровневый приоритет: manual-поле -> dims-поле
 * (pre-computed) -> вычисленная геометрия. Только floorArea/wallArea/perimeter
 * переопределяемы; остальные 8 полей приходят из вычисленной геометрии без
 * изменений.
 *
 * SAFE-02: каждый уровень оверрайда принимается только если это конечное
 * число (`Number.isFinite`) — раньше приоритет шёл через `??`, который
 * пропускает только null/undefined, так что сохранённый NaN/Infinity прошёл
 * бы напрямую в денежный расчёт (Warning из ревью Phase 33). Литеральный ноль
 * конечен, поэтому оверрайд нулём по-прежнему побеждает — семантика Phase 33
 * не меняется.
 *
 * calculator.ts, computeTotals.ts и useEstimate.ts обязаны вызывать эту
 * функцию вместо локального мерджа оверрайдов (TRUTH-01).
 */
export const resolveEffectiveRoomGeometry = (room: {
  dims?: RoomDims;
  openings?: Opening[];
  partitions?: Partition[];
  wallElements?: WallElement[];
}): RoomGeometry => {
  const dims = room.dims || ({} as RoomDims);
  const rawGeom = calculateRoomGeometry(
    dims.length,
    dims.width,
    dims.height,
    room.openings || [],
    room.partitions || [],
    room.wallElements || []
  );

  return {
    ...rawGeom,
    floorArea: firstFinite(dims.manualFloorArea, dims.floorArea) ?? rawGeom.floorArea,
    wallArea: firstFinite(dims.manualWallArea, dims.wallArea) ?? rawGeom.wallArea,
    perimeter: firstFinite(dims.manualPerimeter, dims.perimeter) ?? rawGeom.perimeter,
  };
};

/**
 * Разрешает эффективную ПРОЕКТНУЮ геометрию — единственное место, где комнатные
 * геометрии суммируются в агрегат для глобальных (project-level) позиций, а
 * затем поверх агрегата накладывается проектный оверрайд. Сиблинг
 * `resolveEffectiveRoomGeometry` выше: та же трёхуровневая идея приоритета
 * (оверрайд -> вычисленное), но на уровень проекта.
 *
 * Почему агрегируются только floorArea/wallArea/perimeter, а не все 11 полей:
 * оба движка расчёта (`computeTotals.ts`, `calculator.ts`) и экран уже
 * трактуют проектную геометрию именно так — глобальная позиция ценится по
 * площади пола/стен/периметру проекта, а откосы и прочие 8 полей на уровне
 * проекта контракта не имеют и всегда возвращались нулями. Раздвинуть этот
 * контракт на откосы значило бы изменить поведение экрана для
 * откос-типизированных глобальных позиций — отдельное решение для отдельной
 * фазы (CLAUDE.md правила 2 и 21), сюда не входит.
 *
 * Почему агрегат округляется: `calculateRoomGeometry` уже возвращает
 * `roundTo(x, 2)` на каждое поле; суммируя несколько 2-decimal значений,
 * бинарная арифметика даёт "пыль" (`48.6 + 37.8 -> 86.39999999999999`).
 * `roundTo` на сумме держит агрегат в том же контракте, что и его слагаемые.
 * Это геометрия, а не деньги — денежное правило (округлить строку, сложить
 * уже округлённые строки, сумму не переокруглять) этой функцией не
 * затрагивается и не меняется.
 *
 * Агрегируется из ЭФФЕКТИВНОЙ геометрии каждой комнаты (через
 * `resolveEffectiveRoomGeometry`), а не из сырых `room.dims` — иначе
 * покомнатный ручной оверрайд молча терялся бы на уровне проекта.
 *
 * `calculator.ts`, `computeTotals.ts` и `useEstimate.ts` обязаны вызывать эту
 * функцию вместо локальной агрегации — иначе воспроизводится ровно то
 * расхождение экран/экспорт, ради устранения которого она существует
 * (TRUTH-02).
 *
 * WR-02 (Phase 34 review): каждый проектный оверрайд принимается только если
 * это конечное число, через тот же `firstFinite`, что уже применён на сиблинге
 * `resolveEffectiveRoomGeometry` — раньше этот мердж шёл через незащищённый
 * `??`, который пропускает только null/undefined, так что сохранённый
 * NaN/Infinity прошёл бы напрямую в `useEstimate.ts`, `calculator.ts` и
 * `computeTotals.ts`. Литеральный ноль конечен, поэтому оверрайд нулём
 * по-прежнему побеждает агрегат.
 */
export const resolveEffectiveProjectGeometry = (
  rooms: Array<{
    dims?: RoomDims;
    openings?: Opening[];
    partitions?: Partition[];
    wallElements?: WallElement[];
  }>,
  projectOverrides?: { floorArea?: number; wallArea?: number; perimeter?: number }
): RoomGeometry => {
  let floorAreaSum = 0;
  let wallAreaSum = 0;
  let perimeterSum = 0;

  for (const room of rooms) {
    const geom = resolveEffectiveRoomGeometry(room);
    floorAreaSum += geom.floorArea;
    wallAreaSum += geom.wallArea;
    perimeterSum += geom.perimeter;
  }

  return {
    floorArea: firstFinite(projectOverrides?.floorArea) ?? roundTo(floorAreaSum, 2),
    wallArea: firstFinite(projectOverrides?.wallArea) ?? roundTo(wallAreaSum, 2),
    perimeter: firstFinite(projectOverrides?.perimeter) ?? roundTo(perimeterSum, 2),
    height: 0,
    openingsArea: 0,
    partitionsArea: 0,
    windowSlopes: 0,
    doorSlopes: 0,
    partitionsEndsLinear: 0,
    nichesArea: 0,
    ledgesArea: 0,
  };
};

/**
 * Разрешает количество на основе типа расчета и геометрии
 */
export const resolveUnitQuantity = (unitType: string, geometry: RoomGeometry, manualQty: number = 1): number => {
  switch (unitType) {
    case 'm2_floor': return geometry.floorArea;
    case 'm2_ceiling': return geometry.floorArea; // For now linked to floor, can be decoupled later
    case 'm2_wall': return geometry.wallArea;
    case 'm2': return geometry.wallArea;
    case 'mp': return geometry.perimeter;
    case 'mp_slopes_win': return geometry.windowSlopes;
    case 'mp_slopes_door': return geometry.doorSlopes;
    case 'mp_slopes': return geometry.windowSlopes + geometry.doorSlopes + geometry.partitionsEndsLinear; 
    case 'pcs': return manualQty;
    default: return manualQty;
  }
};
